/* ==========================================================================
   A/C Refrigerant and Oil Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-aoc]');
  if (!root) return;

  /* Oil that stays behind in each component and leaves with it when it is
     replaced. Industry standard figures in fluid ounces. */
  var OIL = { condenser: 1.0, evap: 1.5, drier: 0.5, hose: 0.5 };

  var G_PER_OZ = 28.349523125;
  var OZ_PER_LB = 16;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    var items = [], oil = 0, needTotal = false;

    if (input.compressor) {
      needTotal = true;
    }
    if (input.condenser) { oil += OIL.condenser; items.push(['Condenser', OIL.condenser]); }
    if (input.evap)      { oil += OIL.evap;      items.push(['Evaporator', OIL.evap]); }
    if (input.drier)     { oil += OIL.drier;     items.push(['Receiver drier', OIL.drier]); }
    var hoses = isFinite(input.hoses) && input.hoses > 0 ? Math.round(input.hoses) : 0;
    if (hoses > 0) { oil += hoses * OIL.hose; items.push([hoses + ' hose' + (hoses > 1 ? 's' : ''), hoses * OIL.hose]); }

    var status, warnings = [], figures = [], rows = [];

    if (input.compressor) {
      if (!isFinite(input.total) || input.total <= 0) {
        return { ok: false, errors: ['Replacing the compressor needs the total system oil capacity. It is on the underhood label or in the service manual.'] };
      }
      /* A new compressor usually ships with a full system charge of oil in it.
         Drain it and put back only what the system actually needs. */
      status = { key: 'monitor', label: num(input.total, 1) + ' oz total',
                 note: 'Drain the new compressor completely and add the full system charge back, distributed between the compressor and the low side.' };
      figures.push({ label: 'Total oil for the system', value: num(input.total, 1) + ' oz',
        sub: 'Most new compressors ship shipped full. Drain it and measure back in.' });
      rows.push({ head: 'Compressor replacement' });
      rows.push({ label: 'System capacity', value: num(input.total, 1) + ' oz' });
      rows.push({ label: 'Drain new compressor', value: 'Completely' });
      rows.push({ label: 'Add back', value: num(input.total, 1) + ' oz' });
      warnings.push('Do not simply install a new compressor on top of the oil already in the system. Overfilling with oil reduces cooling capacity and can hydrolock the compressor.');
    } else if (oil > 0) {
      status = { key: 'good', label: num(oil, 1) + ' oz',
                 note: 'Add this to the new component before fitting it, or into the low side on evacuation.' };
      figures.push({ label: 'Oil to add', value: num(oil, 1) + ' oz',
        sub: num(oil * G_PER_OZ, 0) + ' grams, or ' + num(oil / 8, 2) + ' cups.' });
      rows.push({ head: 'Oil by component' });
      items.forEach(function (x) { rows.push({ label: x[0], value: num(x[1], 1) + ' oz' }); });
      rows.push({ label: 'Total to add', value: num(oil, 1) + ' oz' });
    } else {
      status = { key: 'good', label: 'No oil needed',
                 note: 'Nothing selected that carries oil out of the system.' };
      figures.push({ label: 'Oil to add', value: '0 oz',
        sub: 'Select the components you are replacing.' });
    }

    if (isFinite(input.charge) && input.charge > 0) {
      var oz = input.chargeUnit === 'lb' ? input.charge * OZ_PER_LB
             : (input.chargeUnit === 'g' ? input.charge / G_PER_OZ
             : (input.chargeUnit === 'kg' ? (input.charge * 1000) / G_PER_OZ : input.charge));
      figures.push({ label: 'Refrigerant charge', value: num(oz, 1) + ' oz',
        sub: num(oz / OZ_PER_LB, 2) + ' lb, ' + num(oz * G_PER_OZ, 0) + ' g.' });
      rows.push({ head: 'Refrigerant charge' });
      rows.push({ label: 'Ounces', value: num(oz, 2) });
      rows.push({ label: 'Pounds', value: num(oz / OZ_PER_LB, 3) });
      rows.push({ label: 'Grams', value: num(oz * G_PER_OZ, 0) });
      rows.push({ label: 'Kilograms', value: num((oz * G_PER_OZ) / 1000, 3) });
    }

    rows.push({ head: 'Standard oil amounts' });
    rows.push({ label: 'Condenser', value: '1.0 oz' });
    rows.push({ label: 'Evaporator', value: '1.5 oz' });
    rows.push({ label: 'Receiver drier', value: '0.5 oz' });
    rows.push({ label: 'Each hose', value: '0.5 oz' });

    return { ok: true, errors: [], warnings: warnings, status: status, figures: figures, rows: rows };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "total",
          "key": "t",
          "type": "number"
      },
      {
          "name": "compressor",
          "key": "cp",
          "type": "checkbox"
      },
      {
          "name": "condenser",
          "key": "cd",
          "type": "checkbox"
      },
      {
          "name": "evap",
          "key": "ev",
          "type": "checkbox"
      },
      {
          "name": "drier",
          "key": "dr",
          "type": "checkbox"
      },
      {
          "name": "hoses",
          "key": "ho",
          "type": "number"
      },
      {
          "name": "charge",
          "key": "c",
          "type": "number"
      },
      {
          "name": "chargeUnit",
          "key": "cu",
          "type": "select"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-aoc__figure">'
        + '<div class="pc-aoc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-aoc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-aoc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-aoc__row pc-aoc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-aoc__row">'
        + '<span class="pc-aoc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-aoc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_AOC = { compute: compute };
})();
